import {setTitle as setDocumentTitle} from '@github-ui/document-metadata'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {isMobile} from '@github-ui/get-os'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {useStickyHeader} from '@github-ui/use-sticky-header/useStickyHeader'
import {XIcon} from '@primer/octicons-react'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Flash, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import type React from 'react'
import {useEffect, useMemo, useRef, useState} from 'react'

import {ContentWrapper} from '../ContentWrapper'
import type {IssueSecondaryNode} from '../issue-viewer-secondary-query-types'
import type {IssueNode} from '../issue-viewer-view-query-types'
import type {OptionConfig} from '../OptionConfig'
import styles from './Header.module.css'
import type {HeaderBlockedBySummaryFragment} from './HeaderBlockedBySummary'
import {HeaderEditor} from './HeaderEditor'
import {HeaderMetadata} from './HeaderMetadata'
import type {HeaderParentFragment} from './HeaderParentTitle'
import type {HeaderSubIssueSummaryFragment} from './HeaderSubIssueSummary'
import {HeaderViewer} from './HeaderViewer'
import {JumpToBottom} from './JumpToBottom'
import type {LinkedSecurityAlertsFragment} from './LinkedSecurityAlerts'
import type {TrackedByFragment} from './TrackedBy'
import {useTitleMirror} from './use-title-mirror'

export type HeaderSecondaryFragment = RelayCompat<Pick<IssueSecondaryNode, 'isTransferInProgress'>> &
  // Optional: absent until the secondary query resolves, and on the Relay secondary payload.
  Partial<RelayCompat<Pick<IssueSecondaryNode, 'title' | 'titleHTML'>>>
type HeaderRepository = NonNullable<IssueNode['repository']>
type HeaderRepositoryOwner = NonNullable<HeaderRepository['owner']>
type HeaderIssueType = NonNullable<IssueNode['issueType']>
type HeaderClosedByPullRequestNode = NonNullable<
  NonNullable<NonNullable<IssueNode['closedByPullRequestsReferences']>['nodes']>[number]
>

// RELAY-CLEANUP(IssueViewerIssue): widens enum/nullability from the still-Relay parent.
// Remove when IssueViewerIssue leaves Relay.
export type HeaderFragment = RelayCompat<
  Pick<
    IssueNode,
    | 'title'
    | 'titleHTML'
    | 'number'
    | 'url'
    | 'viewerCanUpdateNext'
    | 'id'
    | 'subIssuesSummary'
    | 'state'
    | 'stateReason'
    | 'duplicateOf'
    | 'linkedPullRequests'
    | 'labels'
    | 'milestone'
    | 'assignedActors'
  > & {
    readonly repository: Pick<
      HeaderRepository,
      'name' | 'nameWithOwner' | 'isArchived' | 'isPrivate' | 'isWritable' | 'id'
    > & {
      readonly owner: Pick<HeaderRepositoryOwner, 'login'>
    }
    readonly issueType: Pick<HeaderIssueType, 'name' | 'color'> | null
    readonly agentAssignments: ReadonlyArray<{readonly __typename: string}>
    readonly closedByPullRequestsReferences: {
      readonly nodes: ReadonlyArray<Pick<
        HeaderClosedByPullRequestNode,
        'id' | 'number' | 'url' | 'state' | 'databaseId'
      > | null> | null
    } | null
  }
>

type HeaderBaseProps = {
  optionConfig: OptionConfig
  headerSecondary?: HeaderSecondaryFragment
  blockedBySecondaryData?: HeaderBlockedBySummaryFragment
  trackedBySecondaryData?: TrackedByFragment
  subIssueSecondaryData?: HeaderSubIssueSummaryFragment
  linkedSecurityAlertsData?: LinkedSecurityAlertsFragment
  parent?: HeaderParentFragment | null
}

type HeaderProps = HeaderBaseProps & {
  issue: HeaderFragment
}

export function Header({
  issue,
  headerSecondary,
  blockedBySecondaryData,
  trackedBySecondaryData,
  subIssueSecondaryData,
  linkedSecurityAlertsData,
  parent,
  optionConfig,
}: HeaderProps) {
  // The secondary view's `issue` is the document the optimistic overlay writes through, so the
  // title has to be read from there rather than from the still-Relay primary — and every consumer
  // below reads it off this one object.
  const secondaryTitle = headerSecondary?.title
  const secondaryTitleHTML = headerSecondary?.titleHTML
  // Both or neither: the header renders `titleHTML`, so pairing it with the other document's
  // `title` would show two different titles. Absent until the secondary query resolves.
  const readsSecondaryTitle = secondaryTitle != null && secondaryTitleHTML != null
  const data = useMemo(
    () => (readsSecondaryTitle ? {...issue, title: secondaryTitle, titleHTML: secondaryTitleHTML} : issue),
    [issue, readsSecondaryTitle, secondaryTitle, secondaryTitleHTML],
  )

  // If the secondary data is not available, we default to false
  const isTransferInProgress = headerSecondary?.isTransferInProgress ?? false

  const {
    id,
    title: issueTitle,
    number,
    repository: {nameWithOwner},
  } = data

  const titleRef = useRef<HTMLInputElement>(null)
  const [isIssueTitleEditActive, setIsIssueTitleEditActive] = useState(false)

  // Local title that mirrors the server-provided `issueTitle` when not editing.
  // Updates synchronously in the same render so downstream consumers see the
  // synced value without an extra commit.
  const [title, setTitle] = useTitleMirror(issueTitle, isIssueTitleEditActive)

  const shouldUpdateDocumentTitle =
    optionConfig.selectedProjectId === undefined && !optionConfig.shouldSkipSetDocumentTitle
  useEffect(() => {
    if (issueTitle && shouldUpdateDocumentTitle) {
      setDocumentTitle(`${title} · Issue #${number} · ${nameWithOwner}`)
    }
  }, [shouldUpdateDocumentTitle, issueTitle, nameWithOwner, number, title])

  const onTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const oldValue = title
    const newValue = e.target.value
    if (newValue !== null && newValue !== oldValue) {
      setTitle(newValue)
    }
  }

  // Focus on title when user clicks on edit
  useEffect(() => {
    if (isIssueTitleEditActive && titleRef.current) {
      titleRef.current.focus()
    }
  }, [isIssueTitleEditActive])

  const {isSticky, hasRoots, sensorRef} = useStickyHeader()
  const [transferWarningDismissed, setTransferWarningDismissed] = useState(false)
  const showTransferWarning = isTransferInProgress && !transferWarningDismissed

  return (
    <>
      <ContentWrapper>
        {showTransferWarning && (
          <Flash aria-live="polite" className={styles.transferWarningFlash}>
            Issue transfer in progress
            <IconButton
              variant="invisible"
              aria-label="Dismiss issue is in transfer notification"
              icon={XIcon}
              onClick={() => setTransferWarningDismissed(true)}
            />
          </Flash>
        )}
        {isIssueTitleEditActive ? (
          <HeaderEditor
            issueId={id}
            issueNumber={number}
            nameWithOwner={nameWithOwner}
            title={title}
            titleRef={titleRef}
            onTitleChange={onTitleChange}
            onIssueUpdate={optionConfig.onIssueUpdate}
            cancelIssueTitleEdit={() => {
              setIsIssueTitleEditActive(false)
            }}
            isDirty={issueTitle !== title}
          />
        ) : (
          <HeaderViewer
            headerViewerData={data}
            optionConfig={optionConfig}
            setIsIssueTitleEditActive={setIsIssueTitleEditActive}
            issueTitle={title}
          />
        )}
      </ContentWrapper>
      <HeaderMetadata
        headerMetadataData={data}
        optionConfig={optionConfig}
        isSticky={hasRoots && isSticky}
        setIsIssueTitleEditActive={setIsIssueTitleEditActive}
        linkedSecurityAlertsData={linkedSecurityAlertsData}
        blockedBySecondaryData={blockedBySecondaryData}
        trackedBySecondaryData={trackedBySecondaryData}
        subIssueSecondaryData={subIssueSecondaryData}
        parent={parent}
      />
      {isMobile() && <JumpToBottom />}
      <div ref={sensorRef} className={styles.stickyObservableBox} />
      <div
        className={clsx(
          styles.HeaderContentWrapper,
          isFeatureEnabled('issues_sticky_header') && styles.HeaderContentWrapper_stickyHeader,
          isFeatureEnabled('issue_inline_avatars') && styles.HeaderContentWrapper_inlineAvatars,
        )}
      >
        <ContentWrapper>
          <div className={styles.headerSpacer} />
        </ContentWrapper>
      </div>
    </>
  )
}
